/**
 * Payment Deep Link Utilities
 * Handles opening payment apps via deep links
 */

/**
 * Constructs Android Intent URL to open payment app
 */
export const constructPaymentIntentUrl = (
  orderId: string,
  token: string,
  packageName: string = 'com.phonepe.app'
): string => {
  // Map package names to URL schemes
  const schemeMap: { [key: string]: string } = {
    'com.phonepe.app': 'phonepe',
    'net.one97.paytm': 'paytm',
    'com.google.android.apps.nfc.payment': 'tez', // Google Pay
    'com.phonepe.smartplug': 'phonepe',
  };

  const scheme = schemeMap[packageName] || 'phonepe';
  
  // Construct Intent URL for Android
  const intentUrl = `intent://pay#Intent;` +
    `package=${packageName};` +
    `scheme=${scheme};` +
    `S.orderId=${encodeURIComponent(orderId)};` +
    `S.token=${encodeURIComponent(token)};` +
    `end`;

  return intentUrl;
};

/**
 * Opens payment app using deep link
 */
export const openPaymentApp = (
  mandateUri: string,
  fallbackUrl?: string
): void => {
  // Check if it's already an Intent URL or needs conversion
  if (mandateUri.startsWith('intent://')) {
    // Android Intent URL
    window.location.href = mandateUri;
  } else if (mandateUri.startsWith('http://') || mandateUri.startsWith('https://')) {
    // Universal Link or regular URL
    window.location.href = mandateUri;
  } else if (mandateUri.includes('://')) {
    // Custom URL scheme (phonepe://, paytm://, etc.)
    window.location.href = mandateUri;
  } else {
    // Fallback
    if (fallbackUrl) {
      window.location.href = fallbackUrl;
    } else {
      console.error('Invalid mandateUri format:', mandateUri);
    }
  }
};

/**
 * Detects if device is Android
 */
export const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * Detects if device is iOS
 */
export const isIOS = (): boolean => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * Maps UPI app itemId to package name
 */
export const getAppPackageName = (itemId: string): string => {
  const packageMap: { [key: string]: string } = {
    'phonepe': 'com.phonepe.app',
    'paytm': 'net.one97.paytm',
    'gpay': 'com.google.android.apps.nfc.payment',
    'googlepay': 'com.google.android.apps.nfc.payment',
    'com.phonepe.app': 'com.phonepe.app',
    'net.one97.paytm': 'net.one97.paytm',
  };

  return packageMap[itemId.toLowerCase()] || 'com.phonepe.app';
};

/**
 * Handles UPI Autopay Mandate URI
 * Navigates to PaymentWaiting screen first, then opens the UPI app to create/approve an Autopay Mandate
 * 
 * @param mandateUri - The UPI mandate URI (e.g., "upi://mandate?mn=Autopay&...")
 * @param orderId - Optional order ID to track the payment
 * @param amount - Optional amount for tracking
 * @param appName - Optional app name to display (default: "UPI app")
 * @param navigate - React Router navigate function to navigate to PaymentWaiting screen
 * 
 * @example
 * handleUpiMandateUri(
 *   "upi://mandate?mn=Autopay&ver=01&...",
 *   "PH1764307519391JYMJUZTV",
 *   2,
 *   "PhonePe",
 *   navigate
 * );
 */
export const handleUpiMandateUri = (
  mandateUri: string,
  orderId?: string,
  amount?: number,
  appName: string = 'UPI app',
  navigate?: (path: string, options?: { state?: any }) => void
): void => {
  if (!mandateUri) {
    console.error('Mandate URI is required');
    return;
  }

  // Validate mandate URI format
  if (!mandateUri.startsWith('upi://mandate')) {
    console.warn('Invalid mandate URI format. Expected upi://mandate, got:', mandateUri.substring(0, 20));
    // Still proceed as it might be a valid UPI scheme
  }

  // Store order information in sessionStorage for tracking
  if (orderId) {
    sessionStorage.setItem('pendingOrderId', orderId);
    sessionStorage.setItem('paymentInitiated', 'true');
    sessionStorage.setItem('paymentTimestamp', Date.now().toString());
    sessionStorage.setItem('mandateUri', mandateUri); // Store mandate URI for later use
    
    if (amount) {
      sessionStorage.setItem('pendingPaymentAmount', amount.toString());
    }
  }

  console.log('Preparing to open UPI app for mandate approval:', {
    mandateUri: mandateUri.substring(0, 100) + '...',
    orderId,
    amount,
    appName,
    isMobile: isAndroid() || isIOS()
  });

  // Open UPI app directly from current screen (no navigation before opening)
  // When user returns, they will be navigated to payment-processing screen
  // Mark mandate URI as opened to prevent duplicate opens
  sessionStorage.setItem('mandateUriOpened', 'true');
  sessionStorage.setItem('shouldNavigateToProcessing', 'true'); // Flag to navigate when user returns
  
  // Open UPI app immediately
  try {
    console.log('Opening UPI app with mandate URI...');
    window.location.href = mandateUri;
  } catch (error) {
    console.error('Error redirecting to mandate URI:', error);
    openPaymentApp(mandateUri);
  }
};

